import React, { ChangeEvent, ReactElement, useState } from "react";
import { Link } from "react-router-dom";

import Stack from "veneer/Stack";
import Switch from "veneer/Switch";
import Typography from "veneer/Typography";

import { PRIORITY, Priority } from "./utils";

interface PriorityValue {
  priority: Priority;
}

interface ToggleProps {
  children: ReactElement;
  disabled?: boolean;
  defaultValue?: boolean;
  onChange?: (value: boolean) => void;
}

interface LabelProps {
  hasDefaultValue?: boolean;
  hasNetworkLevel?: boolean;
  networkId: number;
  priority: Priority;
}

function hasOverride(priority: Priority, priorities: PriorityValue[]) {
  return priorities.some((ps) => ps.priority < priority);
}

function isOverriding(priority: Priority, priorities: PriorityValue[]) {
  const hasLevel = priorities.some((ps) => ps.priority === priority);
  const hasLowerPriority = priorities.some((ps) => ps.priority < priority);

  return hasLevel && hasLowerPriority;
}

const Toggle = ({
  children,
  disabled = false,
  defaultValue = false,
  onChange,
}: ToggleProps) => {
  const [isOn, setIsOn] = useState(defaultValue);

  return (
    <Typography component="div" variant="body2">
      <Stack alignItems="center" direction="row" gap={1}>
        <Switch
          checked={isOn}
          dataTestId="Override.Switch"
          disabled={disabled}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const { checked } = event.currentTarget;
            setIsOn(checked);
            onChange?.(checked);
          }}
        />
        {children}
      </Stack>
    </Typography>
  );
};

function Label({
  hasDefaultValue = false,
  hasNetworkLevel = false,
  networkId,
  priority,
}: LabelProps) {
  if (priority >= PRIORITY.DEVICE && hasNetworkLevel) {
    const link = (
      <Link to={`/networks/${networkId}/configure`}>network level</Link>
    );

    return <>Override {link} value.</>;
  }

  if (priority >= PRIORITY.NETWORK && hasDefaultValue) {
    return <>Override default application value.</>;
  }

  return <>Override value.</>;
}

export default {
  hasOverride,
  isOverriding,
  Label,
  Toggle,
};
